import React, { useEffect, useState } from 'react'
import { useLocation } from '@reach/router'
import LangSwitcher from './LangSwitcher'
import AnimationLink from './Link'
import LetsTalkCta from './LetsTalkCta'
import { isServiceNavItemActive, normalizeNavPath } from './ServicesNavItems'

const findActiveBranch = (items, path) =>
  items.find(sub => sub.items?.length && isServiceNavItemActive(sub, path)) ?? null

const ScreenBack = ({ label, onClick }) => (
  <button type="button" className="mobile-nav__screen-back" onClick={onClick}>
    <span className="mobile-nav__screen-back-icon" aria-hidden="true" />
    <span>{label}</span>
  </button>
)

const ScreenForwardChevron = () => (
  <span className="mobile-nav__screen-forward-icon" aria-hidden="true" />
)

const MobileNavSplitRow = ({
  link,
  label,
  isCurrent,
  isSectionCurrent,
  onForward,
  forwardLabel,
  variant = 'screen',
}) => (
  <div className={`mobile-nav__split-row mobile-nav__split-row--${variant}`}>
    <AnimationLink
      paintDrip
      hex="#FFF"
      to={link}
      className={
        (variant === 'root' ? 'link depth-0 ' : '') +
        `mobile-nav__split-row-link${
          isCurrent ? ' mobile-nav__split-row-link--current' : ''
        }${isSectionCurrent ? ' mobile-nav__split-row-link--section-current' : ''}`
      }
      aria-current={isCurrent ? 'page' : undefined}
    >
      {label}
    </AnimationLink>
    <button
      type="button"
      className="mobile-nav__split-row-forward"
      onClick={onForward}
      aria-label={forwardLabel}
    >
      <ScreenForwardChevron />
    </button>
  </div>
)

const MobileServicesListScreen = ({ services, path, isEn, onBack, onOpenBranch }) => {
  const menuLabel = isEn ? 'Menu' : 'Menu'
  const forwardLabel = isEn ? 'Show subpages' : 'Ver subpáginas'

  return (
    <div className="mobile-nav__screen" key="services-list">
      <ScreenBack label={menuLabel} onClick={onBack} />
      <h2 className="mobile-nav__screen-title">{services.name}</h2>
      <ul className="mobile-nav__screen-list">
        {services.items.map(item => {
          const isCurrent = normalizeNavPath(item.link) === path
          const hasChildren = item.items?.length > 0
          const isSectionCurrent = isServiceNavItemActive(item, path)

          if (!hasChildren) {
            return (
              <li key={item.link} className="mobile-nav__screen-item">
                <AnimationLink
                  paintDrip
                  hex="#FFF"
                  to={item.link}
                  className={`mobile-nav__screen-row${
                    isCurrent ? ' mobile-nav__screen-row--current' : ''
                  }`}
                  aria-current={isCurrent ? 'page' : undefined}
                >
                  {item.name}
                </AnimationLink>
              </li>
            )
          }

          return (
            <li key={item.link} className="mobile-nav__screen-item mobile-nav__screen-item--split">
              <MobileNavSplitRow
                link={item.link}
                label={item.name}
                isCurrent={isCurrent}
                isSectionCurrent={isSectionCurrent}
                onForward={() => onOpenBranch(item)}
                forwardLabel={`${forwardLabel}: ${item.name}`}
              />
            </li>
          )
        })}
      </ul>
    </div>
  )
}

const MobileServicesBranchScreen = ({ services, branch, path, onBack }) => (
  <div className="mobile-nav__screen" key={`branch-${branch.link}`}>
    <ScreenBack label={services.name} onClick={onBack} />
    <h2 className="mobile-nav__screen-title">
      <AnimationLink
        paintDrip
        hex="#FFF"
        to={branch.link}
        className={`mobile-nav__screen-title-link${
          normalizeNavPath(branch.link) === path ? ' mobile-nav__screen-title-link--current' : ''
        }`}
        aria-current={normalizeNavPath(branch.link) === path ? 'page' : undefined}
      >
        {branch.name}
      </AnimationLink>
    </h2>
    <ul className="mobile-nav__screen-list">
      {branch.items.map(nested => {
        const isNestedCurrent = normalizeNavPath(nested.link) === path
        return (
          <li key={nested.link} className="mobile-nav__screen-item">
            <AnimationLink
              paintDrip
              hex="#FFF"
              to={nested.link}
              className={`mobile-nav__screen-row${
                isNestedCurrent ? ' mobile-nav__screen-row--current' : ''
              }`}
              aria-current={isNestedCurrent ? 'page' : undefined}
            >
              {nested.name}
            </AnimationLink>
          </li>
        )
      })}
    </ul>
  </div>
)

const MobileMenu = props => {
  const isEn = props.lang === 'en'
  const { pathname } = useLocation()
  const path = normalizeNavPath(pathname)

  const services = props.pages.find(page => page.servicesNav && page.items)
  const [servicesView, setServicesView] = useState(null)

  useEffect(() => {
    setServicesView(null)
  }, [pathname])

  const openServices = () => {
    const branch = services ? findActiveBranch(services.items, path) : null
    setServicesView(branch ? { branch } : 'list')
  }

  const renderToolboxStyleItems = link => (
    <>
      <AnimationLink paintDrip hex="#FFF" className="link depth-0" to={link.link}>
        {link.name}
      </AnimationLink>
      <span className="indent-0"></span>
      {link.items.map(sublink =>
        sublink.items ? (
          <React.Fragment key={sublink.name}>
            <AnimationLink
              paintDrip
              hex="#FFF"
              style={{ marginLeft: `20px` }}
              className="link depth-0"
              to={sublink.link}
            >
              {sublink.name}
            </AnimationLink>
            {sublink.items.map(nestedsublink => (
              <div key={nestedsublink.name}>
                <AnimationLink
                  paintDrip
                  hex="#FFF"
                  style={{ marginLeft: `40px` }}
                  className="link depth-2"
                  to={nestedsublink.link}
                >
                  {nestedsublink.name}
                </AnimationLink>
              </div>
            ))}
          </React.Fragment>
        ) : (
          <div key={sublink.name}>
            <AnimationLink
              paintDrip
              hex="#FFF"
              style={{ marginLeft: `20px` }}
              className="link depth-1"
              to={sublink.link}
            >
              {sublink.name}
            </AnimationLink>
          </div>
        )
      )}
    </>
  )

  if (servicesView === 'list' && services) {
    return (
      <nav className="mobile-nav mobile-nav--screen">
        <MobileServicesListScreen
          services={services}
          path={path}
          isEn={isEn}
          onBack={() => setServicesView(null)}
          onOpenBranch={branch => setServicesView({ branch })}
        />
      </nav>
    )
  }

  if (servicesView?.branch && services) {
    return (
      <nav className="mobile-nav mobile-nav--screen">
        <MobileServicesBranchScreen
          services={services}
          branch={servicesView.branch}
          path={path}
          onBack={() => setServicesView('list')}
        />
      </nav>
    )
  }

  const servicesForwardLabel = isEn ? 'Show services' : 'Ver serviços'

  return (
    <nav className="mobile-nav">
      {props.pages.map((link, index) => {
        if (link.items) {
          if (link.servicesNav) {
            return (
              <div key={link.name} className="mobile-nav__root-item">
                <MobileNavSplitRow
                  variant="root"
                  link={link.link}
                  label={link.name}
                  isCurrent={normalizeNavPath(link.link) === path}
                  isSectionCurrent={services?.items?.some(item => isServiceNavItemActive(item, path))}
                  onForward={openServices}
                  forwardLabel={servicesForwardLabel}
                />
              </div>
            )
          }
          return (
            <React.Fragment key={link.name}>
              {renderToolboxStyleItems(link)}
            </React.Fragment>
          )
        }
        if (props.pages.length === index + 1) {
          return (
            <div key={link.name} className="bg-blue mobile-menu__cta-bar">
              <LangSwitcher />
              <LetsTalkCta isEn={isEn} hex="#FFF" tone="onDark" />
            </div>
          )
        }
        return (
          <div key={link.name}>
            <AnimationLink paintDrip hex="#FFF" className="link depth-0" to={link.link}>
              {link.name}
            </AnimationLink>
          </div>
        )
      })}
    </nav>
  )
}

export default MobileMenu
